import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/dates";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCw, Truck, Factory, Package, Globe, Search,
  ShieldCheck, ShieldOff, Users as UsersIcon, Mail, Trash2, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend,
} from "recharts";

const CHANNEL_LABELS = {
  everlantern: "Everlantern", hong_zheng: "Hong Zheng", newgates: "Newgates", hl: "H&L",
};
const PALETTE = ["#056839", "#A0522D", "#69A488", "#E4D5B7", "#3F3F3F", "#D2E4D6"];

const ROLE_PILL = {
  collector:  "bg-[#D2E4D6] text-[#056839]",
  facility:   "bg-[#E4D5B7] text-[#A0522D]",
  viewer:     "bg-[#69A488]/20 text-[#3F3F3F]",
  admin:      "bg-[#A0522D]/15 text-[#A0522D]",
  superadmin: "bg-[#0F2E2A] text-white",
};
const STATUS_PILL = {
  Active:   "bg-[#D2E4D6] text-[#056839]",
  Pending:  "bg-[#E4D5B7] text-[#A0522D]",
  Inactive: "bg-gray-200 text-gray-600",
  Rejected: "bg-[#A0522D]/20 text-[#A0522D]",
  Suspended:"bg-gray-300 text-gray-700",
};

export default function SuperadminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="superadmin-dashboard">
      <Topbar subtitle="GFS Admin Console" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Superadmin</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-[#000000] mt-1">
            Platform control
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-[#D2E4D6]" data-testid="superadmin-tabs">
          {[
            { v: "overview", l: "Platform Overview" },
            { v: "users",    l: "User Management" },
            { v: "records",  l: "Records" },
            { v: "adjust",   l: "Adjust Batch" },
          ].map(t => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`superadmin-tab-${t.v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.v ? "border-[#056839] text-[#056839]" : "border-transparent text-[#3F3F3F]/70 hover:text-[#056839]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "users" && <UsersTab />}
        {tab === "records" && <RecordsTab />}
        {tab === "adjust" && <AdjustBatchTab />}
      </div>
    </div>
  );
}

// ============================================================================
// Tab 1: Platform Overview
// ============================================================================
function OverviewTab() {
  const [collections, setCollections] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [stock, setStock] = useState(null);
  const [busy, setBusy] = useState(false);

  async function loadAll() {
    setBusy(true);
    try {
      const [c, s, st] = await Promise.all([
        api.get("/admin/collections"),
        api.get("/admin/shipments"),
        api.get("/facility/stock-summary"),
      ]);
      setCollections(c.data || []);
      setShipments(s.data || []);
      setStock(st.data || null);
    } catch (err) {
      // Non-fatal: keep last rendered data, show empty state if first load.
      console.debug("superadmin loadAll failed", err);
    } finally { setBusy(false); }
  }
  useEffect(() => { loadAll(); }, []);

  const totals = useMemo(() => ({
    collections_count: collections.length,
    collections_kg: collections.reduce((a, c) => a + (c.received_weight_kg || c.estimated_weight_kg || 0), 0),
    shipments_count: shipments.length,
    shipments_kg: shipments.reduce((a, s) => a + (s.total_shipped_kg || 0), 0),
  }), [collections, shipments]);

  const byChannel = useMemo(() => {
    const m = {};
    Object.keys(CHANNEL_LABELS).forEach(k => { m[k] = { id: k, name: CHANNEL_LABELS[k], coll: 0, kg_collected: 0, ship: 0, kg_shipped: 0 }; });
    collections.forEach(c => { const k = c.channel_id; if (m[k]) { m[k].coll++; m[k].kg_collected += (c.received_weight_kg || c.estimated_weight_kg || 0); } });
    shipments.forEach(s => { const k = s.channel_id; if (m[k]) { m[k].ship++; m[k].kg_shipped += (s.total_shipped_kg || 0); } });
    return Object.values(m).map(c => ({ ...c, kg_collected: Number(c.kg_collected.toFixed(1)), kg_shipped: Number(c.kg_shipped.toFixed(1)) }));
  }, [collections, shipments]);

  const materialBreakdown = useMemo(() => {
    const m = {};
    collections.forEach(c => { const mat = c.material_type || "Unknown"; m[mat] = (m[mat] || 0) + (c.received_weight_kg || c.estimated_weight_kg || 0); });
    return Object.entries(m).filter(([, kg]) => kg > 0).map(([material, kg]) => ({ material, kg: Number(kg.toFixed(1)) }));
  }, [collections]);

  const monthly = useMemo(() => {
    const m = {};
    collections.forEach(c => {
      if (!c.timestamp) return;
      const d = new Date(c.timestamp);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).sort().map(([month, count]) => ({ month, count }));
  }, [collections]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <button onClick={loadAll} disabled={busy} data-testid="superadmin-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[#D2E4D6] rounded-lg bg-white hover:bg-[#F9F8F6] disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testId="su-kpi-collections" label="Collections" value={totals.collections_count} sub={`${totals.collections_kg.toFixed(0)} kg total`} icon={<Truck className="w-5 h-5" />} />
        <KPI testId="su-kpi-shipments"   label="Shipments"   value={totals.shipments_count}   sub={`${totals.shipments_kg.toFixed(0)} kg total`} icon={<Factory className="w-5 h-5" />} />
        <KPI testId="su-kpi-stock"       label="In stock"    value={stock ? `${stock.total_in_stock_kg.toFixed(0)} kg` : "—"} sub="Across all channels" icon={<Package className="w-5 h-5" />} />
        <KPI testId="su-kpi-channels"    label="Active channels" value={byChannel.filter(c => c.coll + c.ship > 0).length} sub={`of ${byChannel.length}`} icon={<Globe className="w-5 h-5" />} />
      </div>

      <Card className="border-[#D2E4D6]/80 rounded-xl">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="su-channel-table">
            <thead className="bg-[#D2E4D6]/50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
                <th className="px-4 py-3">Channel</th>
                <th className="px-4 py-3 text-right">Collections</th>
                <th className="px-4 py-3 text-right">Kg collected</th>
                <th className="px-4 py-3 text-right">Shipments</th>
                <th className="px-4 py-3 text-right">Kg shipped</th>
              </tr>
            </thead>
            <tbody>
              {byChannel.map(c => (
                <tr key={c.id} className="border-t border-[#D2E4D6]/60" data-testid={`su-channel-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.coll}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.kg_collected.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.ship}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.kg_shipped.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Kg collected per channel" testId="su-chart-coll">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byChannel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="kg_collected" fill="#056839" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Kg shipped per channel" testId="su-chart-ship">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byChannel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="kg_shipped" fill="#A0522D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Material mix" testId="su-chart-materials">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Pie data={materialBreakdown} dataKey="kg" nameKey="material" outerRadius={80} label={(d) => d.material}>
                {materialBreakdown.map((entry, i) => <Cell key={entry.material || `mat-${i}`} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Collections over time (monthly)" testId="su-chart-monthly">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#D2E4D6" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip />
              <Line type="monotone" dataKey="count" stroke="#056839" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0">
          <div className="p-3 border-b border-[#D2E4D6] text-xs uppercase tracking-wider text-[#69A488] font-semibold">Last 20 collections</div>
          <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="su-recent-collections">
            <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider">
              <tr><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-right">Kg</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {collections.slice(0, 20).map(c => (
                <tr key={c.id} className="border-t border-[#D2E4D6]/40">
                  <td className="px-3 py-2">{CHANNEL_LABELS[c.channel_id] || "—"}</td>
                  <td className="px-3 py-2">{c.material_type || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{(c.received_weight_kg || c.estimated_weight_kg || 0).toFixed(1)}</td>
                  <td className="px-3 py-2">{formatDate(c.timestamp)}</td>
                  <td className="px-3 py-2">{c.status}</td>
                </tr>
              ))}
              {collections.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[#3F3F3F]/60">No collections yet.</td></tr>}
            </tbody>
          </table></div>
        </CardContent></Card>

        <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0">
          <div className="p-3 border-b border-[#D2E4D6] text-xs uppercase tracking-wider text-[#69A488] font-semibold">Last 20 shipments</div>
          <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="su-recent-shipments">
            <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider">
              <tr><th className="px-3 py-2 text-left">Channel</th><th className="px-3 py-2 text-left">Destination</th><th className="px-3 py-2 text-right">Kg</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Admin status</th></tr>
            </thead>
            <tbody>
              {shipments.slice(0, 20).map(s => (
                <tr key={s.id} className="border-t border-[#D2E4D6]/40">
                  <td className="px-3 py-2">{CHANNEL_LABELS[s.channel_id] || "—"}</td>
                  <td className="px-3 py-2">{s.destination_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{(s.total_shipped_kg || 0).toFixed(1)}</td>
                  <td className="px-3 py-2">{formatDate(s.created_at)}</td>
                  <td className="px-3 py-2 capitalize">{s.admin_status || "pending"}</td>
                </tr>
              ))}
              {shipments.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[#3F3F3F]/60">No shipments yet.</td></tr>}
            </tbody>
          </table></div>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ============================================================================
// Tab 2: User Management
// ============================================================================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [roleF, setRoleF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [channelF, setChannelF] = useState("");
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  async function load() {
    setBusy(true);
    try {
      const { data } = await api.get("/superadmin/users");
      setUsers(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function patchStatus(u, status) {
    try {
      await api.patch(`/superadmin/users/${u.id}/status`, { status });
      toast.success(`${u.name || u.email} → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function patchChannel(u, channel_id) {
    try {
      await api.patch(`/superadmin/users/${u.id}/channel`, { channel_id: channel_id || null });
      toast.success("Channel updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function patchRole(u, role) {
    try {
      await api.patch(`/superadmin/users/${u.id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }
  async function resendInvite(u) {
    if (!window.confirm(`Resend invite to ${u.email}?\n\nA new temporary password will be emailed and the existing one will stop working immediately.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invite resent to ${u.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }

  const filtered = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    return users.filter(u => {
      if (qLow && !(u.name?.toLowerCase().includes(qLow) || u.email?.toLowerCase().includes(qLow))) return false;
      if (roleF && u.role !== roleF) return false;
      if (statusF && u.status !== statusF) return false;
      if (channelF === "unassigned" && u.channel_id) return false;
      if (channelF && channelF !== "unassigned" && u.channel_id !== channelF) return false;
      return true;
    });
  }, [users, q, roleF, statusF, channelF]);

  return (
    <div className="space-y-4" data-testid="superadmin-users-tab">
      {/* Filter bar */}
      <Card className="border-[#D2E4D6]/80 rounded-xl">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Search</label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#69A488]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="su-users-search" placeholder="Name or email…" className="h-9 pl-9" />
            </div>
          </div>
          <FilterSelect label="Role" value={roleF} onChange={setRoleF} testId="su-users-filter-role"
            options={[["", "All roles"], ["collector", "Collector"], ["facility", "Facility"], ["viewer", "Viewer"], ["admin", "Admin"], ["superadmin", "Superadmin"]]} />
          <FilterSelect label="Status" value={statusF} onChange={setStatusF} testId="su-users-filter-status"
            options={[["", "All statuses"], ["Active", "Active"], ["Pending", "Pending"], ["Inactive", "Inactive"], ["Rejected", "Rejected"]]} />
          <FilterSelect label="Channel" value={channelF} onChange={setChannelF} testId="su-users-filter-channel"
            options={[["", "All channels"], ["everlantern", "Everlantern"], ["hong_zheng", "Hong Zheng"], ["newgates", "Newgates"], ["hl", "H&L"], ["unassigned", "Unassigned"]]} />
          <Button onClick={load} disabled={busy} variant="outline" className="h-9 gap-1.5" data-testid="su-users-refresh">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh users
          </Button>
        </CardContent>
      </Card>

      <div className="text-xs text-[#3F3F3F]/70" data-testid="su-users-count">
        Showing {filtered.length} of {users.length} users
      </div>

      <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="su-users-table">
          <thead className="bg-[#D2E4D6]/50">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]">
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Email</th>
              <th className="px-3 py-3">Role</th>
              <th className="px-3 py-3">Channel</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Last login</th>
              <th className="px-3 py-3">Signed up</th>
              <th className="px-3 py-3 sticky right-0 bg-[#D2E4D6]/95 backdrop-blur-sm text-right shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.08)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => {
              const isSelf = u.role === "superadmin";
              return (
                <tr key={u.id} className="border-t border-[#D2E4D6]/60 align-top" data-testid={`su-user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium text-[#000000]">{u.name || "—"}</td>
                  <td className="px-3 py-2 text-[#3F3F3F]">{u.email}</td>
                  <td className="px-3 py-2">
                    {isSelf ? (
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${ROLE_PILL[u.role] || ""}`}>{u.role}</span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => patchRole(u, e.target.value)}
                        data-testid={`su-role-${u.id}`}
                        className={`h-7 text-[11px] rounded-md border border-[#D2E4D6] bg-white px-1.5 ${ROLE_PILL[u.role] || ""}`}
                      >
                        <option value="collector">collector</option>
                        <option value="facility">facility</option>
                        <option value="viewer">viewer</option>
                        <option value="admin">admin</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isSelf || u.role === "admin" || u.role === "viewer" ? (
                      <span className="text-xs text-[#3F3F3F]/50 italic">n/a</span>
                    ) : (
                      <select
                        value={u.channel_id || ""}
                        onChange={(e) => patchChannel(u, e.target.value)}
                        data-testid={`su-channel-${u.id}`}
                        className="h-7 text-[11px] rounded-md border border-[#D2E4D6] bg-white px-1.5"
                      >
                        <option value="">— none —</option>
                        <option value="everlantern">Everlantern</option>
                        <option value="hong_zheng">Hong Zheng</option>
                        <option value="newgates">Newgates</option>
                        <option value="hl">H&L</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[u.status] || ""}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-[#3F3F3F]/70">
                    {formatDateTime(u.last_login)}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#3F3F3F]/70">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="px-3 py-2 sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.08)] text-right">
                    <div className="inline-flex items-center gap-1.5 justify-end flex-wrap">
                      {!isSelf && (
                        <button
                          onClick={() => resendInvite(u)}
                          data-testid={`su-resend-invite-${u.id}`}
                          title="Issue a new temporary password and email the user a fresh invite. The user will be forced through /first-login on next sign-in."
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                        >
                          <Mail className="w-3 h-3" /> Resend invite
                        </button>
                      )}
                      {isSelf ? (
                        <span className="text-[11px] text-[#3F3F3F]/40 italic">—</span>
                      ) : u.status === "Active" ? (
                        <button
                          onClick={() => setConfirmDeactivate(u)}
                          data-testid={`su-deactivate-${u.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white transition-colors"
                        >
                          <ShieldOff className="w-3 h-3" /> Deactivate
                        </button>
                      ) : (u.status === "Inactive" || u.status === "Pending") ? (
                        <button
                          onClick={() => patchStatus(u, "Active")}
                          data-testid={`su-activate-${u.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white transition-colors"
                        >
                          <ShieldCheck className="w-3 h-3" /> Activate
                        </button>
                      ) : (
                        <span className="text-[11px] text-[#3F3F3F]/40 italic">{u.status}</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-[#3F3F3F]/60">
                <UsersIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />No users match these filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={!!confirmDeactivate} onOpenChange={(o) => !o && setConfirmDeactivate(null)}>
        <DialogContent data-testid="su-deactivate-confirm">
          <DialogHeader>
            <DialogTitle>Deactivate user?</DialogTitle>
          </DialogHeader>
          {confirmDeactivate && (
            <p className="text-sm text-[#3F3F3F]">
              Are you sure you want to deactivate <strong>{confirmDeactivate.name || confirmDeactivate.email}</strong>?
              They will be immediately logged out on their next request.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeactivate(null)} data-testid="su-deactivate-cancel">Cancel</Button>
            <Button
              onClick={async () => { const u = confirmDeactivate; setConfirmDeactivate(null); await patchStatus(u, "Inactive"); }}
              data-testid="su-deactivate-confirm-btn"
              className="bg-[#A0522D] hover:bg-[#804124] text-white"
            >
              Confirm deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-[160px]">
      <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-9 mt-1 w-full text-sm rounded-md border border-[#D2E4D6] bg-white px-3"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function KPI({ label, value, sub, icon, testId }) {
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#69A488] font-medium">{label}</div>
            <div className="font-heading text-3xl font-semibold tracking-tight mt-2 text-[#000000]">{value}</div>
            <div className="text-xs text-[#3F3F3F]/70 mt-1">{sub}</div>
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#D2E4D6] text-[#056839]">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid={testId}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider text-[#69A488] font-semibold mb-3">{title}</div>
        {children}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Tab 3: Records (destructive — superadmin only)
// ============================================================================
function RecordsTab() {
  const [sub, setSub] = useState("collections");
  return (
    <div className="space-y-4" data-testid="superadmin-records-tab">
      <Card className="border-[#A0522D]/30 bg-[#A0522D]/5 rounded-xl">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#A0522D] shrink-0 mt-0.5" />
          <div className="text-xs text-[#3F3F3F]">
            <div className="font-semibold text-[#A0522D] uppercase tracking-wider mb-1">Destructive zone</div>
            Deleting a record is permanent. Records used downstream (collection → processing, processing → shipment) cannot be deleted until the downstream record is removed first. Every deletion is written to the audit log.
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2" data-testid="records-subtabs">
        {[
          { v: "collections", l: "Collections" },
          { v: "processings", l: "Processings" },
          { v: "shipments",   l: "Shipments"   },
        ].map(s => (
          <button
            key={s.v}
            type="button"
            onClick={() => setSub(s.v)}
            data-testid={`records-subtab-${s.v}`}
            className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
              sub === s.v ? "bg-[#056839] text-white shadow-sm" : "text-[#3F3F3F]/70 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>

      {sub === "collections" && <RecordsCollections />}
      {sub === "processings" && <RecordsProcessings />}
      {sub === "shipments"   && <RecordsShipments />}
    </div>
  );
}

function useRecords(url) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(url);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [url]);
  return { rows, busy, reload: load };
}

function DeleteConfirmDialog({ open, onClose, onConfirmed, title, summary, deleteUrl }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setPhrase(""); }, [open]);
  async function doDelete() {
    setBusy(true);
    try {
      await api.delete(deleteUrl);
      toast.success("Deleted");
      onConfirmed();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="records-delete-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#A0522D]">
            <AlertTriangle className="w-5 h-5" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-[#D2E4D6] bg-[#F9F8F6] p-3 text-sm text-[#3F3F3F]">
            {summary}
          </div>
          <p className="text-xs text-[#3F3F3F]/70">
            This action is <strong>permanent</strong>. Type <code className="px-1 py-0.5 bg-[#A0522D]/10 text-[#A0522D] rounded text-[11px]">DELETE</code> to confirm.
          </p>
          <Input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder="DELETE"
            autoFocus
            data-testid="records-delete-phrase"
            className="h-10"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="records-delete-cancel">Cancel</Button>
          <Button
            onClick={doDelete}
            disabled={phrase !== "DELETE" || busy}
            data-testid="records-delete-confirm-btn"
            className="bg-[#A0522D] hover:bg-[#804124] text-white disabled:opacity-50"
          >
            {busy ? "Deleting…" : "Confirm delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordsCollections() {
  const { rows, busy, reload } = useRecords("/admin/collections");
  const [target, setTarget] = useState(null);
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0">
      <div className="p-3 border-b border-[#D2E4D6] flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#69A488] font-semibold">All collections ({rows.length})</div>
        <button onClick={reload} disabled={busy} className="text-xs text-[#056839] hover:underline disabled:opacity-50" data-testid="records-coll-refresh">
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="records-collections-table">
        <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Channel</th>
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-left">Facility</th>
            <th className="px-3 py-2 text-left">Material</th>
            <th className="px-3 py-2 text-right">Kg</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right sticky right-0 bg-[#D2E4D6]/95">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id} className="border-t border-[#D2E4D6]/40" data-testid={`records-coll-row-${c.id}`}>
              <td className="px-3 py-2">{formatDate(c.timestamp)}</td>
              <td className="px-3 py-2">{CHANNEL_LABELS[c.channel_id] || "—"}</td>
              <td className="px-3 py-2 max-w-[180px] truncate" title={c.source_location}>{c.source_location || "—"}</td>
              <td className="px-3 py-2 max-w-[160px] truncate">{c.destination_facility_name || "—"}</td>
              <td className="px-3 py-2">{c.material_type || (c.materials || []).map(m => m.material_type).join(", ") || "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{(c.received_weight_kg || c.total_weight_kg || c.estimated_weight_kg || 0).toFixed(1)}</td>
              <td className="px-3 py-2">{c.status}</td>
              <td className="px-3 py-2 text-right sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.08)]">
                <button
                  onClick={() => setTarget(c)}
                  data-testid={`records-coll-delete-${c.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white transition-colors"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && !busy && (
            <tr><td colSpan={8} className="px-4 py-12 text-center text-[#3F3F3F]/60">No collections.</td></tr>
          )}
        </tbody>
      </table></div>
      <DeleteConfirmDialog
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirmed={reload}
        title="Delete collection?"
        deleteUrl={target ? `/superadmin/collections/${target.id}` : ""}
        summary={target && (
          <div className="space-y-1">
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Source</span>{target.source_location}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Facility</span>{target.destination_facility_name || "—"}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Material</span>{target.material_type || (target.materials || []).map(m => m.material_type).join(", ") || "—"}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Status</span>{target.status}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">ID</span><code className="text-[10px]">{target.id}</code></div>
          </div>
        )}
      />
    </CardContent></Card>
  );
}

function RecordsProcessings() {
  const { rows, busy, reload } = useRecords("/admin/processings");
  const [target, setTarget] = useState(null);
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0">
      <div className="p-3 border-b border-[#D2E4D6] flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#69A488] font-semibold">All processings ({rows.length})</div>
        <button onClick={reload} disabled={busy} className="text-xs text-[#056839] hover:underline disabled:opacity-50" data-testid="records-proc-refresh">
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="records-processings-table">
        <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Facility</th>
            <th className="px-3 py-2 text-left">Type(s)</th>
            <th className="px-3 py-2 text-right">Input kg</th>
            <th className="px-3 py-2 text-right">Outputs</th>
            <th className="px-3 py-2 text-right sticky right-0 bg-[#D2E4D6]/95">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id} className="border-t border-[#D2E4D6]/40" data-testid={`records-proc-row-${p.id}`}>
              <td className="px-3 py-2">{formatDate(p.processing_date)}</td>
              <td className="px-3 py-2 max-w-[180px] truncate">{p.facility_name || "—"}</td>
              <td className="px-3 py-2">{(p.processing_types || []).join(", ") || "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{(p.total_input_weight_kg || 0).toFixed(1)}</td>
              <td className="px-3 py-2 text-right">{(p.outputs || []).length}</td>
              <td className="px-3 py-2 text-right sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.08)]">
                <button
                  onClick={() => setTarget(p)}
                  data-testid={`records-proc-delete-${p.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white transition-colors"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && !busy && (
            <tr><td colSpan={6} className="px-4 py-12 text-center text-[#3F3F3F]/60">No processings.</td></tr>
          )}
        </tbody>
      </table></div>
      <DeleteConfirmDialog
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirmed={reload}
        title="Delete processing?"
        deleteUrl={target ? `/superadmin/processings/${target.id}` : ""}
        summary={target && (
          <div className="space-y-1">
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Facility</span>{target.facility_name || "—"}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Date</span>{formatDate(target.processing_date)}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Outputs</span>{(target.outputs || []).length}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">ID</span><code className="text-[10px]">{target.id}</code></div>
          </div>
        )}
      />
    </CardContent></Card>
  );
}

function RecordsShipments() {
  const { rows, busy, reload } = useRecords("/admin/shipments");
  const [target, setTarget] = useState(null);
  const destNameOf = (s) => s.destination_name || ((s.destinations || [])[0] || {}).destination_name || "—";
  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl"><CardContent className="p-0">
      <div className="p-3 border-b border-[#D2E4D6] flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#69A488] font-semibold">All shipments ({rows.length})</div>
        <button onClick={reload} disabled={busy} className="text-xs text-[#056839] hover:underline disabled:opacity-50" data-testid="records-ship-refresh">
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="records-shipments-table">
        <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Facility</th>
            <th className="px-3 py-2 text-left">Destination</th>
            <th className="px-3 py-2 text-right">Kg</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right sticky right-0 bg-[#D2E4D6]/95">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(s => (
            <tr key={s.id} className="border-t border-[#D2E4D6]/40" data-testid={`records-ship-row-${s.id}`}>
              <td className="px-3 py-2">{formatDate(s.created_at)}</td>
              <td className="px-3 py-2 max-w-[160px] truncate">{s.facility_name || "—"}</td>
              <td className="px-3 py-2 max-w-[180px] truncate">{destNameOf(s)}</td>
              <td className="px-3 py-2 text-right font-mono">{(s.total_shipped_kg || 0).toFixed(1)}</td>
              <td className="px-3 py-2 capitalize">{s.admin_status || "pending"}</td>
              <td className="px-3 py-2 text-right sticky right-0 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.08)]">
                <button
                  onClick={() => setTarget(s)}
                  data-testid={`records-ship-delete-${s.id}`}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white transition-colors"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && !busy && (
            <tr><td colSpan={6} className="px-4 py-12 text-center text-[#3F3F3F]/60">No shipments.</td></tr>
          )}
        </tbody>
      </table></div>
      <DeleteConfirmDialog
        open={!!target}
        onClose={() => setTarget(null)}
        onConfirmed={reload}
        title="Delete shipment?"
        deleteUrl={target ? `/superadmin/shipments/${target.id}` : ""}
        summary={target && (
          <div className="space-y-1">
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Destination</span>{destNameOf(target)}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Facility</span>{target.facility_name || "—"}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Kg shipped</span>{(target.total_shipped_kg || 0).toFixed(1)}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Status</span>{target.admin_status || "pending"}</div>
            <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">ID</span><code className="text-[10px]">{target.id}</code></div>
            <div className="mt-2 text-[11px] text-[#3F3F3F]/70 italic">
              Note: deleting this shipment will free its source output batches so they reappear in the facility&apos;s &quot;available outputs&quot; list.
            </div>
          </div>
        )}
      />
    </CardContent></Card>
  );
}


// -----------------------------------------------------------------------------
// Tab 4: Adjust Batch (manual correction of an output's shipped_weight_kg —
// used to reclaim remaining kg on shipments logged BEFORE the partial-shipping
// refactor went live)
// -----------------------------------------------------------------------------

function AdjustBatchTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [material, setMaterial] = useState("");
  const [output, setOutput] = useState(null);
  const [newShipped, setNewShipped] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status && status !== "all") params.set("status", status);
      if (material) params.set("material_type", material);
      const { data } = await api.get(`/superadmin/outputs?${params.toString()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load outputs");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, material]);

  function pick(row) {
    setOutput(row);
    setNewShipped(String(row.shipped_weight_kg));
    setReason("");
  }

  async function save() {
    if (!output) return;
    const kg = parseFloat(newShipped);
    if (Number.isNaN(kg) || kg < 0) {
      toast.error("Enter a valid non-negative kg value");
      return;
    }
    if (kg - output.weight_kg > 0.001) {
      toast.error(`Cannot exceed batch weight (${output.weight_kg} kg)`);
      return;
    }
    if (!reason.trim()) {
      toast.error("A reason is required for the audit log");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/superadmin/outputs/${output.output_id}/adjust`, {
        shipped_weight_kg: kg,
        reason: reason.trim(),
      });
      toast.success("Adjustment saved. Remaining kg updated.");
      setOutput({ ...output, ...data });
      setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function copyId(id) {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Output ID copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  const newShippedNum = parseFloat(newShipped);
  const previewRemaining =
    output && !Number.isNaN(newShippedNum)
      ? Math.max(0, output.weight_kg - newShippedNum)
      : null;

  const materials = Array.from(new Set(rows.map((r) => r.material_type).filter(Boolean)));

  return (
    <div className="space-y-4" data-testid="superadmin-adjust-tab">
      <Card className="border-[#D2E4D6] rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#000000]">Adjust batch remaining</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#3F3F3F]/70 leading-relaxed">
            Manually set how much of a processing output batch has been consumed. Use this to reclaim
            leftover kg from shipments logged before the partial-shipping refactor went live — the
            one-time migration marked every previously-shipped output as fully consumed, so any real
            remainder needs to be entered here (with a written reason). Every change is recorded in
            the audit log.
          </p>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium block mb-1">Search</label>
              <input
                type="text"
                placeholder="Output ID, processing ID or facility name"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                data-testid="adjust-search-input"
                className="w-full h-9 rounded-md border border-[#D2E4D6] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#056839]/30"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium block mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="adjust-status-filter"
                className="w-full h-9 rounded-md border border-[#D2E4D6] bg-white px-2 text-sm"
              >
                <option value="all">All</option>
                <option value="available">Has remaining</option>
                <option value="consumed">Fully consumed</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium block mb-1">Material</label>
              <select
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                data-testid="adjust-material-filter"
                className="w-full h-9 rounded-md border border-[#D2E4D6] bg-white px-2 text-sm"
              >
                <option value="">All materials</option>
                {materials.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="adjust-refresh-btn"
              className="h-8 px-3 rounded-md border border-[#D2E4D6] text-[#056839] text-xs font-medium hover:bg-[#F0FAF4] disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {/* Results table */}
          <div className="rounded-lg border border-[#D2E4D6] overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#EAF3EC] text-[#3F3F3F] sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Output ID</th>
                    <th className="text-left px-3 py-2 font-medium">Facility</th>
                    <th className="text-left px-3 py-2 font-medium">Material</th>
                    <th className="text-right px-3 py-2 font-medium">Weight</th>
                    <th className="text-right px-3 py-2 font-medium">Shipped</th>
                    <th className="text-right px-3 py-2 font-medium">Remaining</th>
                    <th className="text-left px-3 py-2 font-medium">Processing date</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="adjust-results-body">
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-[#3F3F3F]/60 italic">No output batches match.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.output_id} className={`border-t border-[#D2E4D6] hover:bg-[#F0FAF4] ${output?.output_id === r.output_id ? "bg-[#F0FAF4]" : ""}`} data-testid={`adjust-row-${r.output_id}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">
                        <button
                          type="button"
                          onClick={() => copyId(r.output_id)}
                          title={r.output_id}
                          className="hover:underline text-[#056839]"
                          data-testid={`adjust-copy-${r.output_id}`}
                        >
                          {r.output_id.slice(0, 8)}… <span className="text-[9px] uppercase tracking-wider text-[#69A488]">copy</span>
                        </button>
                      </td>
                      <td className="px-3 py-2 text-[#3F3F3F]/80">{r.facility_name || "—"}</td>
                      <td className="px-3 py-2">{r.material_type} · {r.form}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.weight_kg}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.shipped_weight_kg}</td>
                      <td className={`px-3 py-2 text-right font-mono ${r.remaining_kg > 0 ? "text-[#056839]" : "text-[#3F3F3F]/50"}`}>
                        {r.remaining_kg}
                      </td>
                      <td className="px-3 py-2 text-[#3F3F3F]/70">{r.processing_date ? new Date(r.processing_date).toLocaleDateString() : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => pick(r)}
                          data-testid={`adjust-select-${r.output_id}`}
                          className="h-7 px-3 rounded-md bg-[#056839] text-white text-[11px] font-medium hover:bg-[#0a7a45]"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Adjustment editor */}
          {output && (
            <div className="mt-2 space-y-4 rounded-lg border border-[#D2E4D6] bg-[#F0FAF4] p-4" data-testid="adjust-output-preview">
              <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Selected batch</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="sm:col-span-2 flex items-center gap-2">
                  <span className="text-[#69A488] uppercase text-[10px] tracking-wider">Output ID</span>
                  <code className="text-[11px] break-all">{output.output_id}</code>
                  <button
                    type="button"
                    onClick={() => copyId(output.output_id)}
                    className="text-[10px] uppercase tracking-wider text-[#056839] hover:underline"
                    data-testid="adjust-selected-copy"
                  >
                    copy
                  </button>
                </div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Material</span>{output.material_type} · {output.form}</div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Facility</span>{output.facility_name || "—"}</div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Batch weight</span><span className="font-mono">{output.weight_kg} kg</span></div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Current shipped</span><span className="font-mono" data-testid="adjust-current-shipped">{output.shipped_weight_kg} kg</span></div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Current remaining</span><span className="font-mono text-[#056839]">{output.remaining_kg} kg</span></div>
                <div><span className="text-[#69A488] uppercase text-[10px] tracking-wider mr-2">Fully shipped flag</span>{output.shipped ? "Yes" : "No"}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-[#D2E4D6]">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium block mb-1">
                    New shipped_weight_kg (0 – {output.weight_kg})
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    max={output.weight_kg}
                    value={newShipped}
                    onChange={(e) => setNewShipped(e.target.value)}
                    data-testid="adjust-new-shipped-input"
                    className="w-full h-9 rounded-md border border-[#D2E4D6] bg-white px-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#056839]/30"
                  />
                  {previewRemaining !== null && (
                    <div className="mt-1 text-[11px] text-[#3F3F3F]/70">
                      → new remaining will be{" "}
                      <span className="font-mono text-[#056839]" data-testid="adjust-preview-remaining">
                        {previewRemaining.toFixed(3)} kg
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium block mb-1">
                    Reason (required)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. reconciliation of 2026-05 shipment SHP-abc12345"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="adjust-reason-input"
                    className="w-full h-9 rounded-md border border-[#D2E4D6] bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#056839]/30"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={save}
                disabled={saving || !reason.trim()}
                data-testid="adjust-save-btn"
                className="h-9 px-4 rounded-md bg-[#A0522D] text-white text-sm font-medium hover:bg-[#8b451f] disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save adjustment"}
              </button>
              <p className="text-[11px] text-[#3F3F3F]/60 italic">
                This does not un-do any existing shipment&apos;s destination weights — it only changes
                how much of this specific output is considered consumed. If the new value is less
                than the batch weight, the output will reappear in the facility&apos;s &quot;available outputs&quot; picker.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

